import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authAPI, userAPI } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initData, setInitData] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const res = await userAPI.init();
      setUser(res.data.user);
      setInitData({ usage: res.data.usage, history: res.data.history });
    } catch {
      localStorage.removeItem("session_token");
      setUser(null);
      setInitData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("session_token");
    if (token || document.cookie.includes("session_token")) {
      checkAuth();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (data) => {
    const res = await authAPI.login(data);
    if (res.data.session_token) {
      localStorage.setItem("session_token", res.data.session_token);
    }
    setUser(res.data);
    setInitData(null);
    return res.data;
  };

  const register = async (data) => {
    const res = await authAPI.register(data);
    if (res.data.session_token) {
      localStorage.setItem("session_token", res.data.session_token);
    }
    setUser(res.data);
    setInitData(null);
    return res.data;
  };

  const logout = async () => {
    try { await authAPI.logout(); } catch {}
    localStorage.removeItem("session_token");
    setUser(null);
    setInitData(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth, initData, setInitData }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
};
